import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, AlertCircle, RotateCcw, Loader2, ShoppingBag, Truck, Wallet, CalendarClock, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ItemsEditor, Money, StatusPill } from "@/components/common";
import { StandingEditor, standingText } from "@/components/StandingEditor";
import { PauseManager } from "@/components/PauseManager";
import { UpiPayCard } from "@/components/UpiPayCard";
import { ReferCard } from "@/components/ReferCard";
import { RateDelivery } from "@/components/RateDelivery";
import { OfferBanner } from "@/components/OfferBanner";
import { API, publicApi, errMsg } from "@/lib/api";
import { fmtDate, inr, itemsText } from "@/lib/format";

function ConfirmedSummary({ o }) {
  return (
    <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="portal-confirmed-summary">
      <div className="flex items-center gap-2 font-display text-base font-bold"><CheckCircle2 className="h-5 w-5 text-emerald-700" />{o.source === "standing" ? "Auto-confirmed from your regular order" : "Order confirmed"}</div>
      <div className="mt-2 flex items-center gap-2" data-testid="portal-summary-when"><CalendarClock className="h-4 w-4" />Delivery {fmtDate(o.date)}{o.slot ? `, ${o.slot}` : ""}</div>
      <div className="mt-2 space-y-1 rounded-xl bg-white/70 p-3" data-testid="portal-summary-items">
        {o.items.map((i) => <div key={i.product_id} className="flex justify-between"><span>{i.name}</span><span className="font-mono font-semibold">× {i.qty}</span></div>)}
        <div className="flex justify-between border-t border-emerald-100 pt-1 font-semibold"><span>Total</span><Money value={o.total} /></div>
      </div>
      <div className="mt-2 flex items-center gap-1 text-xs text-emerald-800"><MessageCircle className="h-3.5 w-3.5" />We also send this summary to you on WhatsApp. You can change or skip it below.</div>
    </div>
  );
}

function HolidayCard({ token, info, reload }) {
  const add = async (start, end) => {
    try { await publicApi.post(`/${token}/pauses`, { start, end }); toast.success("Holiday saved. No deliveries on those days."); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async (id) => {
    try { await publicApi.delete(`/${token}/pauses/${id}`); toast.success("Holiday removed"); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="mt-4 rounded-2xl border border-sky-200 bg-white p-4" data-testid="portal-holiday-card">
      <div className="font-display font-semibold text-[#0F382C]">Going on holiday?</div>
      <div className="text-sm text-muted-foreground mb-3">Pause deliveries for your travel dates. We won't deliver or ask you on those days.</div>
      <PauseManager pauses={info.pauses} onAdd={add} onRemove={remove} prefix="portal-pause" />
    </div>
  );
}

function StandingCard({ token, info, reload }) {
  const [v, setV] = useState(info.standing_order);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await publicApi.put(`/${token}/standing`, v);
      toast.success(v.active ? "Regular order saved. You won't be asked every day." : "Regular order turned off");
      setOpen(false);
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-8 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4" data-testid="portal-standing-card">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display font-semibold text-[#0F382C]">Regular order</div>
          <div className="text-sm text-muted-foreground" data-testid="portal-standing-summary">
            {info.standing_order.active ? standingText(info.standing_order, info.products) : "Set a fixed daily order and skip the daily question."}
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => { setV(info.standing_order); setOpen(!open); }} data-testid="portal-standing-edit">{open ? "Close" : info.standing_order.active ? "Change" : "Set up"}</Button>
      </div>
      {open && (
        <div className="mt-4">
          <StandingEditor value={v} onChange={setV} products={info.products} prefix="portal-standing" />
          <Button disabled={busy} onClick={save} className="mt-3 h-12 w-full rounded-xl bg-[#0F382C] hover:bg-[#134E3A]" data-testid="portal-standing-save">Save regular order</Button>
        </div>
      )}
    </div>
  );
}

function OrderTab({ token, info, reload }) {
  const existing = info.tomorrow_order;
  const [items, setItems] = useState([]);
  const [slot, setSlot] = useState("");
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  useEffect(() => { setEditing(false); }, [existing]);
  const locked = existing?.status === "confirmed" && !editing;
  useEffect(() => { setSlot(existing?.slot || info.preferred_slot || info.slots?.[0] || ""); }, [existing, info.preferred_slot, info.slots]);
  useEffect(() => {
    setItems(existing?.status === "confirmed" ? existing.items.map((i) => ({ product_id: i.product_id, qty: i.qty })) : info.usual_items);
  }, [existing, info.usual_items]);
  const total = items.reduce((s, it) => s + (info.products.find((p) => p.id === it.product_id)?.price || 0) * it.qty, 0);
  const submit = async (skipped) => {
    setBusy(true);
    try {
      await publicApi.post(`/${token}/order`, { date: info.tomorrow, items, skipped, slot: skipped ? null : slot || null });
      toast.success(skipped ? "Noted: no batter tomorrow" : "Order confirmed! Summary sent on WhatsApp.");
      reload();
      if (!skipped) window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="portal-order-tab">
      <div className="text-xs uppercase tracking-[0.2em] text-[#D96B43]">Tomorrow · {fmtDate(info.tomorrow)}</div>
      <h2 className="font-display text-2xl font-bold text-[#0F382C] mt-1">Do you need batter tomorrow?</h2>
      {info.tomorrow_paused && !existing && (
        <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-800" data-testid="portal-paused-banner">
          You're on holiday tomorrow, so no delivery is planned. Want batter anyway? Pick below and confirm.
        </div>
      )}
      {existing?.status === "confirmed" && <ConfirmedSummary o={existing} />}
      {locked && (
        <button type="button" onClick={() => setEditing(true)} className="mt-3 w-full rounded-2xl border border-[#E7E2D6] bg-white py-3 text-sm font-semibold text-[#0F382C] transition-colors hover:border-[#0F382C]" data-testid="portal-change-order-btn">Change or cancel tomorrow's order</button>
      )}
      {existing && existing.status !== "confirmed" && (
        <div className="mt-4 rounded-2xl bg-slate-100 p-4 text-sm text-slate-700" data-testid="portal-existing-order">
          You said no batter for tomorrow. Changed your mind? Pick below.
        </div>
      )}
      {!locked && <>
      <div className="mt-5"><ItemsEditor products={info.products} value={items} onChange={setItems} prefix="portal" /></div>
      {info.slots?.length > 0 && (
        <div className="mt-5" data-testid="portal-slot-picker">
          <div className="text-sm font-semibold text-[#0F382C] mb-2">Delivery time tomorrow morning</div>
          <div className="flex flex-wrap gap-2">
            {info.slots.map((sl, i) => (
              <button key={sl} type="button" onClick={() => setSlot(sl)} data-testid={`portal-slot-${i}`}
                className={`rounded-full border px-4 py-2 text-sm font-semibold transition-colors ${slot === sl ? "border-[#0F382C] bg-[#0F382C] text-white" : "border-[#E7E2D6] bg-white text-[#0F382C]"}`}>{sl}</button>
            ))}
          </div>
        </div>
      )}
      {info.usual_items.length > 0 && (
        <button className="mt-3 inline-flex items-center text-sm font-semibold text-[#0F382C]" onClick={() => setItems(info.usual_items)} data-testid="portal-same-as-usual"><RotateCcw className="mr-1 h-4 w-4" />Same as usual</button>
      )}
      <div className="sticky bottom-0 -mx-5 mt-6 border-t border-[#E7E2D6] bg-[#FBF9F4]/95 px-5 py-4 backdrop-blur-md">
        <div className="flex justify-between text-sm mb-3"><span>Total</span><Money value={total} className="font-semibold text-lg" testid="portal-order-total" /></div>
        <Button disabled={busy || !items.length} onClick={() => submit(false)} className="h-14 w-full rounded-2xl bg-[#0F382C] hover:bg-[#134E3A] text-base" data-testid="portal-confirm-order-btn">
          {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <><CheckCircle2 className="mr-2 h-5 w-5" />Yes, deliver tomorrow</>}
        </Button>
        <Button variant="outline" disabled={busy} onClick={() => submit(true)} className="mt-2 h-12 w-full rounded-2xl" data-testid="portal-skip-btn">No batter tomorrow</Button>
      </div>
      </>}
      <StandingCard token={token} info={info} reload={reload} />
      <HolidayCard token={token} info={info} reload={reload} />
      <ReferCard token={token} />
    </div>
  );
}

function DeliveriesTab({ token }) {
  const [orders, setOrders] = useState(null);
  const [issue, setIssue] = useState({});
  const load = useCallback(() => publicApi.get(`/${token}/deliveries`).then((r) => setOrders(r.data.orders)), [token]);
  useEffect(() => { load(); }, [load]);
  const confirm = async (o, status) => {
    try { await publicApi.post(`/${token}/deliveries/${o.id}/confirm`, { status, note: issue[o.id] || "" }); toast.success(status === "received" ? "Thanks for confirming!" : "Issue reported. We'll call you."); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!orders) return <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin" />;
  return (
    <div className="space-y-4" data-testid="portal-deliveries-tab">
      <h2 className="font-display text-2xl font-bold text-[#0F382C]">Your deliveries</h2>
      {orders.length === 0 && <div className="rounded-2xl border border-dashed p-8 text-center text-sm text-muted-foreground">No deliveries yet.</div>}
      {orders.map((o) => (
        <div key={o.id} className="rounded-2xl border border-[#E7E2D6] bg-white p-4 space-y-3" data-testid={`portal-delivery-${o.id}`}>
          <div className="flex justify-between"><div className="font-semibold">{fmtDate(o.date)}</div><StatusPill status={o.delivery_status} /></div>
          <div className="text-sm text-muted-foreground">{itemsText(o.items)} · {inr(o.total)}</div>
          {o.delivery_status === "delivered" && <img src={`${API}/public/${token}/photo/${o.id}`} alt="Delivery proof" className="w-full max-h-72 rounded-xl object-cover bg-slate-100" data-testid={`portal-photo-${o.id}`} />}
          {o.delivery_status === "delivered" && !o.customer_confirmation && (
            <>
              <Button onClick={() => confirm(o, "received")} className="h-12 w-full rounded-xl bg-[#0F382C] hover:bg-[#134E3A]" data-testid={`portal-received-${o.id}`}><CheckCircle2 className="mr-2 h-4 w-4" />Yes, I received it</Button>
              <Textarea placeholder="Problem? Tell us (optional)" value={issue[o.id] || ""} onChange={(e) => setIssue({ ...issue, [o.id]: e.target.value })} data-testid={`portal-issue-note-${o.id}`} />
              <Button variant="outline" onClick={() => confirm(o, "issue")} className="w-full rounded-xl border-rose-200 text-rose-700" data-testid={`portal-issue-${o.id}`}><AlertCircle className="mr-2 h-4 w-4" />Report a problem</Button>
            </>
          )}
          {o.customer_confirmation && <StatusPill status={o.customer_confirmation} label={o.customer_confirmation === "received" ? "You confirmed receipt" : "Issue reported"} />}
          {o.delivery_status === "delivered" && <RateDelivery token={token} order={o} onSaved={load} />}
        </div>
      ))}
    </div>
  );
}

function PayTab({ token }) {
  const [days, setDays] = useState(7);
  const [st, setSt] = useState(null);
  const [paying, setPaying] = useState(false);
  useEffect(() => {
    publicApi.post(`/${token}/pay-sync`).catch(() => {}).finally(() =>
      publicApi.get(`/${token}/statement`, { params: { days } }).then((r) => setSt(r.data)));
  }, [token, days]);
  const payOnline = async () => {
    setPaying(true);
    try { const { data } = await publicApi.post(`/${token}/pay-link`); window.location.href = data.short_url; }
    catch (e) { toast.error(errMsg(e)); setPaying(false); }
  };
  if (!st) return <Loader2 className="mx-auto mt-10 h-6 w-6 animate-spin" />;
  return (
    <div data-testid="portal-pay-tab">
      <div className="rounded-3xl bg-[#0F382C] p-6 text-white">
        <div className="text-xs uppercase tracking-[0.2em] text-emerald-200">Amount due</div>
        <div className="font-mono text-4xl font-semibold mt-2" data-testid="portal-balance">{inr(st.balance)}</div>
        {st.razorpay_enabled && st.balance > 0 && (
          <button onClick={payOnline} disabled={paying} className="mt-5 block w-full rounded-2xl bg-[#FBF9F4] py-4 text-center font-bold text-[#0F382C] shadow-md disabled:opacity-60" data-testid="portal-razorpay-btn">
            {paying ? "Opening secure payment..." : `Pay ${inr(st.balance)} online (UPI / Card / Netbanking)`}
          </button>
        )}
        {st.balance > 0 && !st.razorpay_enabled && !st.business.upi_id && <div className="mt-4 text-sm text-emerald-100/80">Please pay cash to the delivery person or contact us.</div>}
        {st.balance <= 0 && <div className="mt-4 text-sm text-emerald-200">All paid. Thank you!</div>}
      </div>
      <UpiPayCard token={token} st={st} />
      <div className="mt-6 flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold text-[#0F382C]">Statement</h3>
        <div className="flex gap-1">{[7, 30].map((d) => <button key={d} onClick={() => setDays(d)} className={`rounded-full px-3 py-1 text-xs font-semibold ${days === d ? "bg-[#0F382C] text-white" : "bg-white border"}`} data-testid={`portal-days-${d}`}>{d} days</button>)}</div>
      </div>
      <div className="mt-3 rounded-2xl border border-[#E7E2D6] bg-white divide-y">
        <div className="flex justify-between p-3 text-sm text-muted-foreground"><span>Opening balance</span><Money value={st.opening} /></div>
        {st.entries.map((e) => (
          <div key={e.id} className="flex justify-between p-3 text-sm">
            <div><div>{e.note}</div><div className="text-xs text-muted-foreground">{fmtDate(e.date)}</div></div>
            <div className="text-right">{e.debit ? <Money value={e.debit} /> : <Money value={-e.credit} className="text-emerald-700" />}<div className="text-xs text-muted-foreground">Bal <Money value={e.balance} /></div></div>
          </div>
        ))}
        <div className="flex justify-between p-3 text-sm font-semibold"><span>Closing balance</span><Money value={st.closing} /></div>
      </div>
    </div>
  );
}

const TABS = [["order", "Order", ShoppingBag], ["deliveries", "Deliveries", Truck], ["pay", "Bill & Pay", Wallet]];

export default function Portal() {
  const { token, tab = "order" } = useParams();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const load = useCallback(() => publicApi.get(`/${token}`).then((r) => setInfo(r.data)).catch((e) => setError(errMsg(e))), [token]);
  useEffect(() => { load(); }, [load]);
  if (error) return <div className="min-h-screen grid place-items-center p-6 text-center" data-testid="portal-error"><div><AlertCircle className="mx-auto h-10 w-10 text-rose-500" /><p className="mt-3">{error}</p></div></div>;
  if (!info) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-[#0F382C]" /></div>;
  return (
    <div className="min-h-screen bg-[#FBF9F4]" data-testid="customer-portal">
      <div className="mx-auto max-w-md px-5 pt-8 pb-4">
        <div className="flex items-center gap-3">
          {info.business.logo_url && <img src={info.business.logo_url} alt="logo" className="h-11 w-11 object-contain" data-testid="portal-logo" />}
          <div>
            <div className="font-display text-lg font-extrabold text-[#0F382C]" data-testid="portal-business-name">{info.business.business_name}</div>
            <div className="text-xs text-muted-foreground">{info.business.tagline}</div>
          </div>
        </div>
        <div className="mt-6 font-display text-3xl font-bold text-[#0F172A]">Namaste, {info.customer.name.split(" ")[0]}</div>
        <div className="mt-6 grid grid-cols-3 gap-1 rounded-2xl bg-white p-1 border border-[#E7E2D6]">
          {TABS.map(([k, l, Icon]) => (
            <button key={k} onClick={() => nav(`/c/${token}/${k}`)} className={`flex items-center justify-center gap-1 rounded-xl py-2.5 text-sm font-semibold transition-colors ${tab === k ? "bg-[#0F382C] text-white" : "text-slate-600"}`} data-testid={`portal-tab-${k}`}>
              <Icon className="h-4 w-4" />{l}
            </button>
          ))}
        </div>
        <div className="mt-6 bf-rise" key={tab}>
          {tab === "order" && <><OfferBanner token={token} /><OrderTab token={token} info={info} reload={load} /></>}
          {tab === "deliveries" && <DeliveriesTab token={token} />}
          {tab === "pay" && <PayTab token={token} />}
        </div>
      </div>
    </div>
  );
}
